/**
 * Webhook Subscriptions
 * Feb 2026
 *
 * Sits under /app/settings/developer/webhooks.
 * Managed with the same "JWT-session only" rule as PATs (server rejects
 * PAT holders on this endpoint set).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Webhook, Plus, Trash2, Zap, ShieldCheck, ExternalLink, Copy, AlertTriangle, KeyRound } from "lucide-react";
import PageHeader from "@/components/PageHeader";

const SubNav = ({ active }) => (
  <div className="flex items-center gap-4 border-b border-border pb-1" data-testid="developer-subnav">
    <a href="/app/developer" className={`px-3 py-1.5 text-sm ${active==='tokens'?'font-medium border-b-2 border-primary text-foreground':'text-muted-foreground hover:text-foreground'}`}>Tokens</a>
    <a href="/app/developer/webhooks" className={`px-3 py-1.5 text-sm ${active==='webhooks'?'font-medium border-b-2 border-primary text-foreground':'text-muted-foreground hover:text-foreground'}`}>Webhooks</a>
    <a href="/app/developer/docs" className={`px-3 py-1.5 text-sm ${active==='docs'?'font-medium border-b-2 border-primary text-foreground':'text-muted-foreground hover:text-foreground'}`}>API Docs</a>
  </div>
);

export default function DeveloperWebhooks() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [eventCatalog, setEventCatalog] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [inspecting, setInspecting] = useState(null);
  const [freshSecret, setFreshSecret] = useState(null);

  const load = async () => {
    try {
      const [w, e] = await Promise.all([
        api.get("/developer/webhooks"),
        api.get("/developer/webhook-events"),
      ]);
      setRows(w.data || []);
      setEventCatalog(e.data?.events || []);
    } catch (err) { toast.error(apiErrorMessage(err, "Load failed")); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  const toggleActive = async (r) => {
    try {
      await api.patch(`/developer/webhooks/${r.id}`, { active: !r.active });
      toast.success(r.active ? "Paused" : "Resumed");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };

  const sendTest = async (r) => {
    try {
      await api.post(`/developer/webhooks/${r.id}/test`);
      toast.success("Test event dispatched — check your endpoint");
      setTimeout(load, 1500);
    } catch (e) { toast.error(apiErrorMessage(e, "Test send failed")); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete subscription "${r.name}"? Any integration using it will stop receiving events.`)) return;
    try {
      await api.delete(`/developer/webhooks/${r.id}`);
      toast.success("Subscription deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const rotate = async (r) => {
    if (!window.confirm("Rotate the signing secret? The old secret stops working immediately — you'll see the new one exactly once.")) return;
    try {
      const { data } = await api.post(`/developer/webhooks/${r.id}/rotate-secret`);
      setFreshSecret({ ...r, secret: data.secret });
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Rotate failed")); }
  };

  return (
    <div className="pb-8">
      <PageHeader icon={KeyRound} title="Developer" subtitle="Outbound webhook subscriptions." />
      <div className="px-4 sm:px-6 md:px-10 space-y-4" data-testid="developer-webhooks-page">
        <SubNav active="webhooks" />
      <div className="border border-primary/40 bg-primary/5 p-5 flex items-start gap-4">
        <Webhook className="w-6 h-6 text-primary shrink-0 mt-0.5" />
        <div className="space-y-1 flex-1">
          <div className="label-eyebrow">Outbound webhooks</div>
          <p className="text-sm text-muted-foreground">
            Get an HMAC-signed HTTPS POST every time an event fires so your integration reacts
            in real-time. We retry once on 5xx/timeout and auto-disable after 10 consecutive failures.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="rounded-none whitespace-nowrap"
                 data-testid="webhook-create-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New subscription
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center"
             data-testid="webhook-empty-state">
          <Webhook className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <div className="font-semibold">No subscriptions yet</div>
          <p className="text-sm text-muted-foreground mt-1">
            Subscribe your integration URL to <span className="font-mono">task.completed</span>,{" "}
            <span className="font-mono">meeting.created</span>, or{" "}
            <span className="font-mono">attendance.clocked_out</span>.
          </p>
        </div>
      ) : (
        <div className="border border-border overflow-x-auto"
             data-testid="webhook-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest bg-muted/20 text-muted-foreground">
                <th className="text-left px-3 py-2 font-semibold">Name / URL</th>
                <th className="text-left px-3 py-2 font-semibold">Events</th>
                <th className="text-left px-3 py-2 font-semibold w-24">Status</th>
                <th className="text-left px-3 py-2 font-semibold w-40">Last success</th>
                <th className="text-left px-3 py-2 font-semibold w-20">Failures</th>
                <th className="w-56"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border align-top"
                    data-testid={`webhook-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-[10px] text-muted-foreground font-mono truncate max-w-[280px]">{r.url}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(r.events || []).map((e) => (
                        <span key={e} className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 font-mono">{e}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.active ? <span className="text-emerald-600">Active</span> : (
                      <span className="text-orange-600 inline-flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />{r.auto_disabled_at ? "Auto-disabled" : "Paused"}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.last_success_at ? new Date(r.last_success_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-2 text-xs">{r.failure_count || 0}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => sendTest(r)}
                              className="text-[11px] px-2 py-1 border border-border hover:bg-muted"
                              title="Send a synthetic test event"
                              data-testid={`webhook-test-${r.id}`}>
                        <Zap className="w-3 h-3 inline mr-1" />Test
                      </button>
                      <button onClick={() => setInspecting(r)}
                              className="text-[11px] px-2 py-1 border border-border hover:bg-muted"
                              data-testid={`webhook-deliveries-${r.id}`}>Deliveries</button>
                      <button onClick={() => toggleActive(r)}
                              className="text-[11px] px-2 py-1 border border-border hover:bg-muted"
                              data-testid={`webhook-toggle-${r.id}`}>{r.active ? "Pause" : "Resume"}</button>
                      <button onClick={() => rotate(r)}
                              className="text-[11px] px-2 py-1 border border-border hover:bg-muted"
                              title="Rotate signing secret"
                              data-testid={`webhook-rotate-${r.id}`}>Rotate</button>
                      <button onClick={() => del(r)}
                              className="text-red-600 hover:bg-red-50 p-1"
                              title="Delete"
                              data-testid={`webhook-delete-${r.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateWebhookDialog
          wsId={activeWorkspaceId}
          eventCatalog={eventCatalog}
          onClose={() => setShowCreate(false)}
          onCreated={(doc) => { setShowCreate(false); setFreshSecret(doc); load(); }}
        />
      )}
      {inspecting && (
        <DeliveriesDrawer sub={inspecting} onClose={() => setInspecting(null)} />
      )}
      {freshSecret && (
        <FreshSecretDialog sub={freshSecret} onClose={() => setFreshSecret(null)} />
      )}

      {/* Feb 2026 · Verification recipes — critical for third-party
          integrators. If they don't verify the signature they're
          trusting anyone who guesses the URL. */}
      <VerificationRecipes />
      </div>
    </div>
  );
}

function VerificationRecipes() {
  return (
    <div className="border border-border p-5 mt-4" data-testid="webhook-verification-recipes">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-4 h-4 text-primary" />
        <div className="label-eyebrow">Verify the signature on your endpoint</div>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Every request carries <code>X-Acquilix-Signature: sha256=&lt;hex&gt;</code>. Compute HMAC-SHA256 of the raw request body with your subscription's signing secret and compare using a constant-time equality check. Reject the request if they don't match.
      </p>
      <div className="grid md:grid-cols-3 gap-3 text-xs">
        <div className="border border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/20 px-2 py-1">Node.js / Express</div>
          <pre className="p-2 text-[11px] overflow-x-auto whitespace-pre-wrap" data-testid="verify-recipe-node">
{`const crypto = require("crypto");

// Body must be RAW bytes — mount raw body-parser for this route.
app.post("/hook", express.raw({ type: "application/json" }), (req, res) => {
  const sig = req.header("X-Acquilix-Signature") || "";
  const expected = "sha256=" +
    crypto.createHmac("sha256", process.env.ACQ_SECRET)
          .update(req.body).digest("hex");
  const ok = crypto.timingSafeEqual(
    Buffer.from(sig), Buffer.from(expected));
  if (!ok) return res.status(401).end();
  const payload = JSON.parse(req.body);
  // handle payload.event, payload.data
  res.status(204).end();
});`}
          </pre>
        </div>
        <div className="border border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/20 px-2 py-1">Python / Flask</div>
          <pre className="p-2 text-[11px] overflow-x-auto whitespace-pre-wrap" data-testid="verify-recipe-python">
{`import hmac, hashlib, os
from flask import Flask, request, abort

app = Flask(__name__)

@app.post("/hook")
def hook():
    raw = request.get_data()  # raw bytes
    sig = request.headers.get("X-Acquilix-Signature", "")
    expected = "sha256=" + hmac.new(
        os.environ["ACQ_SECRET"].encode(), raw,
        hashlib.sha256).hexdigest()
    if not hmac.compare_digest(sig, expected):
        abort(401)
    payload = request.get_json()
    # handle payload["event"], payload["data"]
    return "", 204`}
          </pre>
        </div>
        <div className="border border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/20 px-2 py-1">Go / net/http</div>
          <pre className="p-2 text-[11px] overflow-x-auto whitespace-pre-wrap" data-testid="verify-recipe-go">
{`import (
  "crypto/hmac"; "crypto/sha256"
  "encoding/hex"; "io"; "net/http"; "os"
)

func hook(w http.ResponseWriter, r *http.Request) {
  body, _ := io.ReadAll(r.Body)
  mac := hmac.New(sha256.New,
    []byte(os.Getenv("ACQ_SECRET")))
  mac.Write(body)
  expected := "sha256=" + hex.EncodeToString(mac.Sum(nil))
  got := r.Header.Get("X-Acquilix-Signature")
  if !hmac.Equal([]byte(got), []byte(expected)) {
    w.WriteHeader(401); return
  }
  // decode + handle
  w.WriteHeader(204)
}`}
          </pre>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mt-3">
        Also honour the <code>X-Acquilix-Timestamp</code> header — reject requests older than ~5 minutes to defend against replay attacks. Store the <code>X-Acquilix-Delivery</code> UUID for at least 24 hours to make handling idempotent.
      </p>
    </div>
  );
}

function CreateWebhookDialog({ wsId, eventCatalog, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const toggle = (k) => {
    const s = new Set(events);
    if (s.has(k)) s.delete(k); else s.add(k);
    setEvents(s);
  };
  const submit = async () => {
    if (!name.trim() || !url.trim() || !events.size) {
      toast.error("Name, URL and at least one event required"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/developer/webhooks", {
        name: name.trim(), workspace_id: wsId, url: url.trim(),
        events: Array.from(events),
      });
      onCreated(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg rounded-none" data-testid="webhook-create-dialog">
        <DialogHeader>
          <DialogTitle>New webhook subscription</DialogTitle>
          <DialogDescription>Pick the events you want and the URL to POST them to.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-none mt-1"
                    placeholder="Slack #ops / Zapier hook"
                    data-testid="webhook-create-name" />
          </div>
          <div>
            <Label className="label-eyebrow">HTTPS endpoint</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} className="rounded-none mt-1"
                    placeholder="https://example.com/hooks/acquilix"
                    data-testid="webhook-create-url" />
          </div>
          <div>
            <Label className="label-eyebrow">Events</Label>
            <div className="mt-1 border border-border p-3 space-y-2" data-testid="webhook-create-events">
              {eventCatalog.map((ev) => (
                <label key={ev.key} className="flex items-start gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={events.has(ev.key)}
                          onChange={() => toggle(ev.key)}
                          className="mt-0.5"
                          data-testid={`webhook-event-${ev.key}`} />
                  <span>
                    <span className="font-mono text-xs">{ev.key}</span>
                    <span className="text-muted-foreground"> — {ev.label}</span>
                    {ev.desc && <div className="text-[11px] text-muted-foreground/80">{ev.desc}</div>}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-none"
                   data-testid="webhook-create-submit">
            {busy ? "Creating…" : "Create subscription"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FreshSecretDialog({ sub, onClose }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(sub.secret); toast.success("Secret copied"); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg rounded-none" data-testid="webhook-fresh-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" /> Save your signing secret
          </DialogTitle>
          <DialogDescription>
            Use this secret to verify the <code>X-Acquilix-Signature</code> header (<code>sha256=&lt;hex hmac(secret, body)&gt;</code>) on every incoming request.
          </DialogDescription>
        </DialogHeader>
        <div className="border border-border bg-muted/30 p-3 font-mono text-xs break-all"
              data-testid="webhook-fresh-secret">
          {sub.secret}
        </div>
        <Button onClick={copy} className="rounded-none" data-testid="webhook-fresh-copy">
          <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy secret
        </Button>
        <DialogFooter>
          <Button onClick={onClose} className="rounded-none" data-testid="webhook-fresh-done">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeliveriesDrawer({ sub, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/developer/webhooks/${sub.id}/deliveries`)
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
  }, [sub.id]);
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl rounded-none" data-testid="webhook-deliveries-dialog">
        <DialogHeader>
          <DialogTitle>Recent deliveries · {sub.name}</DialogTitle>
          <DialogDescription>Last 50 attempts (30-day retention).</DialogDescription>
        </DialogHeader>
        {rows === null ? (
          <div className="text-sm text-muted-foreground py-6">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm italic text-muted-foreground py-6">
            No deliveries yet. Click <span className="font-semibold">Test</span> to fire a synthetic event.
          </div>
        ) : (
          <div className="border border-border max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest bg-muted/20 text-muted-foreground">
                  <th className="text-left px-3 py-1.5">Time</th>
                  <th className="text-left px-3 py-1.5">Event</th>
                  <th className="text-left px-3 py-1.5 w-20">Status</th>
                  <th className="text-left px-3 py-1.5 w-20">Code</th>
                  <th className="text-left px-3 py-1.5">Error</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="px-3 py-1.5">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="px-3 py-1.5 font-mono">{r.event}</td>
                    <td className={`px-3 py-1.5 ${r.ok ? "text-emerald-600" : "text-red-600"}`}>{r.ok ? "OK" : "FAIL"}</td>
                    <td className="px-3 py-1.5">{r.status_code ?? "—"}</td>
                    <td className="px-3 py-1.5 text-muted-foreground truncate max-w-[220px]" title={r.error || ""}>{r.error || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button onClick={onClose} className="rounded-none">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
